import axios from "axios";

import type { CreateSessionResponse as AccountsCreateSessionResponse } from "@/providers/smarta-accounts/accounts-api-client";
import { getConfig } from "~/config";
import { AuthApiError } from "./errors";

export type IdentityProvider = "google" | "microsoft";

export interface CreateSessionRequest {
  provider: IdentityProvider;
  idToken: string;
}

/*
 * The response shape comes from the generated accounts client, so a renamed
 * field (such as `identityId` becoming `operatorAccountId`) fails the type
 * check here instead of at runtime.
 */
export type CreateSessionResponse = AccountsCreateSessionResponse;

export type SessionSummary = CreateSessionResponse["session"];

const endpointUrl = (): string =>
  new URL(
    "api/kontax/accounts/api/sessions",
    getConfig("VITE_APP_API_URL")
  ).toString();

export const createSession = async (
  request: CreateSessionRequest
): Promise<CreateSessionResponse> => {
  try {
    const response = await axios.post<CreateSessionResponse>(
      endpointUrl(),
      {
        provider: request.provider,
        idToken: request.idToken,
        clientType: "backoffice",
      },
      {
        headers: { "Content-Type": "application/json" },
        validateStatus: (status) => status === 201,
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? null;
      const upstreamMessage = error.response?.data?.message;
      throw new AuthApiError(
        `Failed to create session${
          upstreamMessage ? `: ${upstreamMessage}` : ""
        }`,
        { status, upstreamMessage }
      );
    }
    throw error;
  }
};
